import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import MemoryRoundedIcon from '@mui/icons-material/MemoryRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { copilotRemoteApi } from '../../services/copilotRemoteApi';
import './remoteMachines.css';

const statusMeta = {
  created: { label: 'Created', color: '#64748b' },
  online: { label: 'Online', color: '#16a34a' },
  offline: { label: 'Offline', color: '#d97706' },
  archived: { label: 'Archived', color: '#dc2626' },
};

const emptyDraft = {
  id: null,
  name: '',
  description: '',
};

const formatDate = (value) => {
  if (!value) return 'Never';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown';
  return parsed.toLocaleString();
};

const getInstallCommand = (machineKey) =>
  `python stealthnet_agent.py --server ${window.location.origin} --machine-key ${machineKey || '<machine-key>'}`;

function StatusChip({ status }) {
  const meta = statusMeta[String(status || '').toLowerCase()] || statusMeta.created;
  return (
    <Chip
      size="small"
      label={meta.label}
      sx={{
        bgcolor: alpha(meta.color, 0.11),
        color: meta.color,
        border: `1px solid ${alpha(meta.color, 0.18)}`,
        fontWeight: 900,
      }}
    />
  );
}

function RemoteMachines() {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [createdMachine, setCreatedMachine] = useState(null);
  const [error, setError] = useState('');

  const activeMachines = useMemo(
    () => machines.filter((machine) => String(machine.status || '').toLowerCase() !== 'archived'),
    [machines]
  );

  const loadMachines = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setMachines(await copilotRemoteApi.listMachines());
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMachines();
  }, [loadMachines]);

  const openCreateDialog = () => {
    setDraft(emptyDraft);
    setCreatedMachine(null);
    setDialogOpen(true);
  };

  const openEditDialog = (machine) => {
    setDraft({
      id: machine.id,
      name: machine.name || '',
      description: machine.description || '',
    });
    setCreatedMachine(null);
    setDialogOpen(true);
  };

  const saveMachine = async () => {
    if (!draft.name.trim()) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: draft.name.trim(),
        description: draft.description.trim(),
      };
      const saved = draft.id
        ? await copilotRemoteApi.updateMachine(draft.id, payload)
        : await copilotRemoteApi.createMachine(payload);
      setCreatedMachine(saved);
      await loadMachines();
      if (draft.id) setDialogOpen(false);
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  };

  const regenerateKey = async (machine) => {
    if (!window.confirm(`Regenerate the machine key for "${machine.name}"? The old key will stop working.`)) return;
    setError('');
    try {
      const updated = await copilotRemoteApi.updateMachine(machine.id, { regenerate_machine_key: true });
      setCreatedMachine(updated);
      setDialogOpen(true);
      await loadMachines();
    } catch (updateError) {
      setError(updateError.message);
    }
  };

  const archiveMachine = async (machine) => {
    if (!window.confirm(`Archive "${machine.name}"?`)) return;
    setError('');
    try {
      await copilotRemoteApi.archiveMachine(machine.id);
      await loadMachines();
    } catch (archiveError) {
      setError(archiveError.message);
    }
  };

  const copyText = async (value) => {
    await navigator.clipboard?.writeText(value);
  };

  return (
    <Box className="remote-machines-page">
      <Card className="remote-machines-hero">
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2}>
            <Box>
              <Typography className="remote-machines-eyebrow">Copilot Infrastructure</Typography>
              <Typography className="remote-machines-title">Remote Machines</Typography>
              <Typography className="remote-machines-copy">
                Register machines that Copilot can use for approved shell-command workflows.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip icon={<MemoryRoundedIcon />} label={`${activeMachines.length} active machines`} className="remote-machines-chip" />
              <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={loadMachines} className="remote-machines-secondary-button">
                Refresh
              </Button>
              <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={openCreateDialog} className="remote-machines-primary-button">
                Add Machine
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }} onClose={() => setError('')}>{error}</Alert> : null}

      {loading ? (
        <Card className="remote-machines-loading">
          <CircularProgress />
          <Typography>Loading remote machines...</Typography>
        </Card>
      ) : null}

      {!loading ? (
        <Grid container spacing={2}>
          {machines.map((machine) => (
            <Grid item xs={12} md={6} xl={4} key={machine.id}>
              <Card className="remote-machine-card">
                <CardContent>
                  <Stack direction="row" spacing={1.2} alignItems="flex-start">
                    <Avatar className="remote-machine-avatar">
                      <MemoryRoundedIcon />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography className="remote-machine-name">{machine.name || `Machine ${machine.id}`}</Typography>
                          <Typography className="remote-machine-description">{machine.description || 'No description provided.'}</Typography>
                        </Box>
                        <StatusChip status={machine.status} />
                      </Stack>
                      <Box className="remote-machine-detail-grid">
                        <Box>
                          <Typography className="remote-machine-label">Last check-in</Typography>
                          <Typography className="remote-machine-value">{formatDate(machine.last_checkin_at)}</Typography>
                        </Box>
                        <Box>
                          <Typography className="remote-machine-label">Created</Typography>
                          <Typography className="remote-machine-value">{formatDate(machine.created_at)}</Typography>
                        </Box>
                      </Box>
                      <Box className="remote-machine-key-row">
                        <KeyRoundedIcon fontSize="small" />
                        <Typography className="remote-machine-key">{machine.machine_key || 'No key returned'}</Typography>
                        <Tooltip title="Copy machine key" arrow>
                          <IconButton size="small" onClick={() => copyText(machine.machine_key || '')}>
                            <ContentCopyRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Box className="remote-machine-install">
                        <Typography className="remote-machine-label">Install / run command</Typography>
                        <pre>{getInstallCommand(machine.machine_key)}</pre>
                      </Box>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                        <Button size="small" variant="outlined" startIcon={<EditNoteRoundedIcon />} onClick={() => openEditDialog(machine)} className="remote-machines-secondary-button">
                          Edit
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<KeyRoundedIcon />} onClick={() => regenerateKey(machine)} className="remote-machines-secondary-button">
                          Regenerate Key
                        </Button>
                        <Button size="small" variant="outlined" color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={() => archiveMachine(machine)} className="remote-machines-secondary-button">
                          Archive
                        </Button>
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {!machines.length ? (
            <Grid item xs={12}>
              <Card className="remote-machines-empty">
                <CardContent>
                  <Typography className="remote-machines-title">No machines yet</Typography>
                  <Typography className="remote-machines-copy">Add a remote machine before asking Copilot to run shell commands.</Typography>
                  <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={openCreateDialog} className="remote-machines-primary-button">
                    Add Machine
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ) : null}
        </Grid>
      ) : null}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            {draft.id ? 'Edit Remote Machine' : 'Add Remote Machine'}
            <IconButton onClick={() => setDialogOpen(false)}><CloseRoundedIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Name" value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} fullWidth />
            <TextField label="Description" value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} fullWidth multiline minRows={3} />
            {createdMachine?.machine_key ? (
              <Box className="remote-machine-created-box">
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircleRoundedIcon color="success" />
                  <Typography sx={{ fontWeight: 900 }}>Machine key ready</Typography>
                </Stack>
                <Box className="remote-machine-key-row">
                  <KeyRoundedIcon fontSize="small" />
                  <Typography className="remote-machine-key">{createdMachine.machine_key}</Typography>
                  <IconButton size="small" onClick={() => copyText(createdMachine.machine_key)}>
                    <ContentCopyRoundedIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box className="remote-machine-install">
                  <Typography className="remote-machine-label">Install / run command</Typography>
                  <pre>{getInstallCommand(createdMachine.machine_key)}</pre>
                </Box>
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDialogOpen(false)} className="remote-machines-secondary-button">Close</Button>
          <Button variant="contained" disabled={saving || !draft.name.trim()} onClick={saveMachine} className="remote-machines-primary-button">
            {saving ? 'Saving...' : draft.id ? 'Save Machine' : 'Create Machine'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RemoteMachines;
